<script setup>
    /**
     * Child component of PaymentInfo, it's an edit form. After saving, it switches to showing.
     * also extracts error message from the mess given by the backend, in an attempt to show the
     * message under the erroneous field and highlight it.
     */

    // Kunden dürfen nur SEPA eintragen, auch wenn sie bisher Bankeinzug nutzten. Für Überweisung muss man uns kontaktieren.
    // Admins können auswählen zwischen SEPA und Überweisung. Bankeinzug soll basically weg.
    // Langfristig soll dank SWIFT-API auch die BIC nicht mehr angegeben werden müssen.

    import { ref, reactive, watchEffect } from 'vue';
    import { getFromAPI, saveFormViaAPI, getFullUrl } from '../../functionCollection';
    import { vMaska } from 'maska';

    const props = defineProps(['fetchUrl', 'updateUrl', 'userNumber', 'isAdmin']);
    const emit = defineEmits(['switch-to-show', 'successful-save', 'save-error']);

    const paymentInfo = ref(null);
    const showError = ref(false);
    const errorMessage = ref(null);
    const bindedObject = reactive({});
    const result = ref();

    // fetch initial data.
    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl, props.userNumber);
        paymentInfo.value = await getFromAPI(url);
        paymentInfo.value.Zahlart = 'SEPA-Basislastschrift (CORE)';
    });

    // attempt to save form data. needs unmasked IBAN (IBAN is shown to users in 4-character blocks).
    // when successful, switch to show, otherwise parse and show error.
    async function saveForm() {
        const formData = JSON.parse(JSON.stringify(paymentInfo.value));
        formData.IBAN = bindedObject.unmasked;
        result.value = await saveFormViaAPI(getFullUrl(props.updateUrl, props.userNumber), formData);
        if (result.value.success) {
            emit('successful-save');
            emit('switch-to-show');
        } else if (result.value.success === false) {
            parseErrorMessage(result.value.data);
        }
    }

    // parses error message so that it can be shown properlz.
    function parseErrorMessage(error) {
        let parsedError = error.response.data;
        errorMessage.value = getErrorType(parsedError);
        errorMessage.value = JSON.parse(JSON.stringify(errorMessage.value));
        showError.value = true;
    }

    // used to know which field has an error in order to not crash the component with undefined values.
    // better solution possible.
    function getErrorType(parsedError) {
        if (typeof parsedError.general !== 'undefined') return parsedError.general[0];
        if (typeof parsedError.IBAN !== 'undefined') {
            return parsedError.IBAN[0];
        }
        if (typeof parsedError.Zahlart !== 'undefined') {
            return parsedError.Zahlart[0];
        }
        emit('save-error');
    }

</script>

<template>
    <div class="well" v-if="paymentInfo">
        <form>
            <h4>Bankverbindung</h4>
            <p>
                Bitte tragen Sie hier nur Daten ein, wenn Sie Ihre Bankverbindung aktualisieren möchten.
                Aus Sicherheitsgründen wird Ihre Bankverbindung nicht angezeigt.
            </p>

            <div class="form-group row" v-if="props.isAdmin == 'true'">
                <label class="col-sm-3 col-form-label">Zahlart</label>
                <div class="col-sm-9">
                    <select v-model="paymentInfo.Zahlart" class="form-select form-control">
                        <option value="SEPA-Basislastschrift (CORE)">SEPA-Basislastschrift (CORE)</option>
                        <option value="Überweisung">Überweisung</option>
                    </select>
                </div>
            </div>

            <div v-if="paymentInfo.Zahlart === 'SEPA-Basislastschrift (CORE)'">
                <div class="form-group row">
                    <label class="col-sm-3 col-form-label">IBAN</label>
                    <div class="col-sm-9">
                        <input
                            name="IBAN"
                            v-maska="bindedObject"
                            v-model="paymentInfo.IBAN"
                            data-maska="@@## **** **** **** **** **** **** **** **"
                            class="form-control"
                            type="text"
                            id="input-IBAN"
                        />
                    </div>


                </div>
            </div>

            <div v-if="paymentInfo.Zahlart === 'Überweisung'">
                <br>
                <p>Bei Überweisung muss nichts weiteres eingetragen werden.</p>
            </div>

            <div v-if="props.isAdmin === 'true'">
                <div class="form-group row">
                    <div class="col-sm-12">
                        <div class="header" style="padding: 0px; padding-top:10px; border-bottom: none;">
                            <div class="checkbox-container">
                                <input type="checkbox" id="notifyCheckbox" v-model="paymentInfo.notify" />
                                <div class="check"></div>
                            </div>
                            <span>Nutzer benachrichtigen</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="alert alert-danger" role="alert" v-if="showError">{{ errorMessage }}</div>
        </form>

        <div class="table-footer">
            <button class="btn btn-link footer-button"
                type="submit"
                @click="saveForm">
                Speichern
                <i class="far fa-lg fa-save"></i>
            </button>
            <button class="btn btn-link footer-button"
                type="submit"
                @click="$emit('switch-to-show')">
                Abbrechen
                <i class="fas fa-lg fa-times"></i>
            </button>
        </div>

    </div>
</template>
